import { useState, useEffect } from 'react';
import { XMarkIcon, PlusIcon, TrashIcon, BoltIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import AsyncSelect from 'react-select/async';
import api from '../services/api';
import JSONEditor from './JSONEditor';

const nozzleToDot = (key) => key.replace('_', '.');

// Fetch slicer base settings with q-param fallback.
// When q is set (extra selected without filamentType) and results are empty, retry without q.
// Returns { data, fallback } — fallback=true means q was dropped to get results.
const fetchBaseSettings = async (params) => {
  const res = await api.get('/auth/slicer-settings/base', { params });
  const data = res.data?.data || [];
  if (data.length === 0 && params.q) {
    const { q: _q, ...paramsWithoutQ } = params;
    const fallbackRes = await api.get('/auth/slicer-settings/base', { params: paramsWithoutQ });
    return { data: fallbackRes.data?.data || [], fallback: true };
  }
  return { data, fallback: false };
};

const getDisplayName = (name) => {
  if (!name) return '';
  if (typeof name === 'string') return name;
  if (typeof name === 'object') {
    const values = Object.values(name);
    return values.find(v => v) || '';
  }
  return String(name);
};

export default function SlicerFilamentForm({ filament, onSave, onCancel }) {
  const [materials, setMaterials] = useState([]);
  const [materialExtras, setMaterialExtras] = useState([]);
  const [machineModels, setMachineModels] = useState([]);
  const [formData, setFormData] = useState({
    uid: '',
    name: '',
    material: '',
    materialExtra: null,
    settings: [],
    notes: '',
    isActive: true
  });
  const [loading, setLoading] = useState(false);
  const [autoLoading, setAutoLoading] = useState(false);
  const [error, setError] = useState('');
  const [systemSettingsOptions, setSystemSettingsOptions] = useState([]);
  const [filteredOptions, setFilteredOptions] = useState({});
  const [expandedOverrides, setExpandedOverrides] = useState({});

  useEffect(() => {
    fetchMaterials();
    fetchMaterialExtras();
    fetchMachineModels();
    // Fetch unfiltered defaults
    api.get('/auth/slicer-settings/base', { params: { type: 'filament' } })
      .then(res => {
        const opts = (res.data?.data || []).map(item => ({ value: item._id, label: item.name }));
        setSystemSettingsOptions(opts);
      })
      .catch(err => console.error('Failed to fetch system settings:', err));
  }, []);

  const fetchMachineModels = async () => {
    try {
      const res = await api.get('/auth/machine-models/by-vendor');
      const groups = res.data?.data || res.data || [];
      setMachineModels(groups.flatMap(g => g.models || []));
    } catch (err) {
      console.error('Failed to fetch machine models:', err);
    }
  };

  const getMaterialFilamentType = () => {
    const mat = materials.find(m => m._id === formData.material);
    return mat?.filamentType || null;
  };

  const getMaterialExtraFilamentType = () => {
    const extra = materialExtras.find(m => m._id === formData.materialExtra);
    return extra?.filamentType || null;
  };

  // When extra is selected but has no filamentType, return its key for q-param fallback
  const getExtraQFallback = (extraId) => {
    const extra = materialExtras.find(m => m._id === (extraId ?? formData.materialExtra));
    if (extra && !extra.filamentType) return extra.key || null;
    return null;
  };

  const getMachineModelName = (modelId) => {
    return machineModels.find(m => m._id === modelId)?.name || null;
  };

  // Fetch filtered options per (machine, nozzle) when dependencies change
  useEffect(() => {
    const materialKey = getMaterialFilamentType();
    const materialExtraKey = getMaterialExtraFilamentType();
    const extraQ = getExtraQFallback();
    const nozzles = ['0_2', '0_4', '0_6', '0_8'];
    const machineIds = formData.settings.map(s => s.machine).filter(Boolean);

    if (machineIds.length === 0 && !materialKey) return;

    const fetches = [];
    machineIds.forEach(machineId => {
      const modelName = getMachineModelName(machineId);
      nozzles.forEach(nozzle => {
        const params = { type: 'filament' };
        if (materialKey) params.filamentType = materialKey;
        if (materialExtraKey) params.filamentType += `-${materialExtraKey}`;
        if (extraQ) params.q = extraQ;
        if (modelName) {
          params.printerModel = modelName;
          params.nozzleSize = nozzleToDot(nozzle);
        }
        fetches.push(
          fetchBaseSettings(params)
            .then(({ data }) => ({
              machineId,
              nozzle,
              options: data.map(item => ({ value: item._id, label: item.name }))
            }))
            .catch(() => ({ machineId, nozzle, options: [] }))
        );
      });
    });

    Promise.all(fetches).then(results => {
      const newOpts = {};
      results.forEach(({ machineId, nozzle, options }) => {
        if (!newOpts[machineId]) newOpts[machineId] = {};
        newOpts[machineId][nozzle] = options;
      });
      setFilteredOptions(newOpts);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-filter when material/extra/machine selection changes
  }, [formData.material, formData.materialExtra, formData.settings.map(s => s.machine).join(','), machineModels, materials, materialExtras]);

  useEffect(() => {
    if (filament) {
      setFormData({
        uid: filament.uid || '',
        name: filament.name || '',
        material: typeof filament.material === 'object' ? filament.material._id : filament.material || '',
        materialExtra: typeof filament.materialExtra === 'object' && filament.materialExtra !== null ? filament.materialExtra._id : filament.materialExtra || null,
        settings: filament.settings || [],
        notes: filament.notes || '',
        isActive: filament.isActive ?? true
      });
    }
  }, [filament]);

  const fetchMaterials = async () => {
    try {
      const response = await api.get('/auth/materials');
      setMaterials(response.data.data || []);
    } catch (err) {
      console.error('Failed to fetch materials:', err);
    }
  };

  const fetchMaterialExtras = async () => {
    try {
      const response = await api.get('/auth/material-extras');
      setMaterialExtras(response.data.data || []);
    } catch (err) {
      console.error('Failed to fetch material extras:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Clean up empty settings
      const cleanedData = {
        ...formData,
        settings: formData.settings.filter(s => s.machine)
      };
      await onSave(cleanedData);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save slicer filament settings');
    } finally {
      setLoading(false);
    }
  };

  const reloadExistingSettings = async (materialId, materialExtraId, currentSettings) => {
    const activeSettings = currentSettings.filter(s => s.machine);
    if (!materialId || activeSettings.length === 0) return;

    const mat = materials.find(m => m._id === materialId);
    const materialKey = mat?.filamentType || null;
    const matExtra = materialExtras.find(m => m._id === materialExtraId);
    const materialExtraKey = matExtra?.filamentType || null;
    const extraQ = getExtraQFallback(materialExtraId);

    const nozzles = ['0_2', '0_4', '0_6', '0_8'];
    const newSettings = currentSettings.map(s => ({ ...s }));

    setAutoLoading(true);
    try {
      const fetches = [];
      newSettings.forEach((setting, idx) => {
        if (!setting.machine) return;
        const modelName = getMachineModelName(setting.machine);
        nozzles.forEach(nozzle => {
          const params = { type: 'filament' };
          if (materialKey) params.filamentType = materialKey;
          if (materialExtraKey) params.filamentType += `-${materialExtraKey}`;
          if (extraQ) params.q = extraQ;
          if (modelName) {
            params.printerModel = modelName;
            params.nozzleSize = nozzleToDot(nozzle);
          }
          fetches.push(
            fetchBaseSettings(params)
              .then(({ data, fallback }) => ({
                settingIdx: idx,
                nozzle,
                options: data.map(item => ({ value: item._id, label: item.name })),
                fallback
              }))
              .catch(() => ({ settingIdx: idx, nozzle, options: [], fallback: false }))
          );
        });
      });

      const results = await Promise.all(fetches);

      // Always overwrite inherits since material context changed, unless results came from q-fallback
      results.forEach(({ settingIdx, nozzle, options, fallback }) => {
        if (options.length === 0 || fallback) return;
        const currentNozzle = newSettings[settingIdx].settings?.[nozzle] || { inherits: null, override: '{}' };
        newSettings[settingIdx] = {
          ...newSettings[settingIdx],
          settings: {
            ...newSettings[settingIdx].settings,
            [nozzle]: { ...currentNozzle, inherits: options[0].value }
          }
        };
      });

      setFormData(prev => ({ ...prev, settings: newSettings }));
    } catch (err) {
      console.error('Failed to reload settings:', err);
    } finally {
      setAutoLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    setFormData(prev => ({ ...prev, [name]: newValue }));

    if (name === 'material' || name === 'materialExtra') {
      const newMaterialId = name === 'material' ? value : formData.material;
      const newMaterialExtraId = name === 'materialExtra' ? value : formData.materialExtra;
      reloadExistingSettings(newMaterialId, newMaterialExtraId, formData.settings);
    }
  };

  const handleSettingChange = (index, nozzle, field, value) => {
    const newSettings = [...formData.settings];
    if (!nozzle) {
      // Machine field change
      newSettings[index] = { ...newSettings[index], machine: value };
    } else {
      // Nozzle-specific setting change
      const currentNozzleSettings = newSettings[index].settings?.[nozzle] || { inherits: null, override: '{}' };
      newSettings[index] = {
        ...newSettings[index],
        settings: {
          ...newSettings[index].settings,
          [nozzle]: {
            ...currentNozzleSettings,
            [field]: field === 'override' ? JSON.stringify(value) : value
          }
        }
      };
    }
    setFormData({ ...formData, settings: newSettings });
  };

  const createLoadOptions = (machineId, nozzleKey) => async (inputValue) => {
    try {
      const params = { type: 'filament' };
      const materialKey = getMaterialFilamentType();
      const materialExtraKey = getMaterialExtraFilamentType();
      const extraQ = getExtraQFallback();
      if (materialKey) params.filamentType = materialKey;
      if (materialExtraKey) params.filamentType += `-${materialExtraKey}`;
      // User input takes priority; fall back to extra key when extra has no filamentType
      params.q = inputValue || extraQ || '';
      const modelName = getMachineModelName(machineId);
      if (modelName) {
        params.printerModel = modelName;
        params.nozzleSize = nozzleToDot(nozzleKey);
      }
      const { data } = await fetchBaseSettings(params);
      return data.map(item => ({ value: item._id, label: item.name }));
    } catch (err) {
      console.error('Failed to fetch system settings:', err);
      return [];
    }
  };

  const autoLoadConfigs = async () => {
    if (machineModels.length === 0) return;
    setAutoLoading(true);
    try {
      const materialKey = getMaterialFilamentType();
      const materialExtraKey = getMaterialExtraFilamentType();
      const extraQ = getExtraQFallback();
      const nozzles = ['0_2', '0_4', '0_6', '0_8'];

      // Build full settings list: keep existing, add missing machines
      const existingMachineIds = new Set(formData.settings.map(s => s.machine).filter(Boolean));
      const newSettings = [...formData.settings];
      machineModels.forEach(model => {
        if (!existingMachineIds.has(model._id)) {
          newSettings.push({ machine: model._id, settings: {} });
        }
      });

      // Fetch first available option for every machine/nozzle combo in parallel
      const fetches = [];
      newSettings.forEach((setting, idx) => {
        if (!setting.machine) return;
        const modelName = getMachineModelName(setting.machine);
        nozzles.forEach(nozzle => {
          const params = { type: 'filament' };
          if (materialKey) params.filamentType = materialKey;
          if (materialExtraKey) params.filamentType += `-${materialExtraKey}`;
          if (extraQ) params.q = extraQ;
          if (modelName) {
            params.printerModel = modelName;
            params.nozzleSize = nozzleToDot(nozzle);
          }
          fetches.push(
            fetchBaseSettings(params)
              .then(({ data, fallback }) => ({
                settingIdx: idx,
                nozzle,
                options: data.map(item => ({ value: item._id, label: item.name })),
                fallback
              }))
              .catch(() => ({ settingIdx: idx, nozzle, options: [], fallback: false }))
          );
        });
      });

      const results = await Promise.all(fetches);

      // Pre-select first option for each nozzle (only if not already set and not from q-fallback)
      results.forEach(({ settingIdx, nozzle, options, fallback }) => {
        if (options.length === 0 || fallback) return;
        const currentNozzle = newSettings[settingIdx].settings?.[nozzle] || { inherits: null, override: '{}' };
        if (!currentNozzle.inherits) {
          newSettings[settingIdx] = {
            ...newSettings[settingIdx],
            settings: {
              ...newSettings[settingIdx].settings,
              [nozzle]: { ...currentNozzle, inherits: options[0].value }
            }
          };
        }
      });

      setFormData(prev => ({ ...prev, settings: newSettings }));
    } catch (err) {
      console.error('Failed to auto-load configs:', err);
    } finally {
      setAutoLoading(false);
    }
  };

  const addSetting = () => {
    setFormData({
      ...formData,
      settings: [
        ...formData.settings,
        {
          machine: '',
          settings: {}
        }
      ]
    });
  };

  const removeSetting = (index) => {
    const newSettings = formData.settings.filter((_, i) => i !== index);
    setFormData({ ...formData, settings: newSettings });
  };

  return (
    <div className="fixed inset-0 bg-slate-900 bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
          <h2 className="text-2xl font-bold text-slate-900">
            {filament ? 'Edit Slicer Filament Settings' : 'Add Slicer Filament Settings'}
          </h2>
          <button
            onClick={onCancel}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="px-6 py-6 space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="uid" className="block text-sm font-medium text-slate-700 mb-2">
                  UID * (max 12 chars, unique per user)
                </label>
                <input
                  type="text"
                  id="uid"
                  name="uid"
                  value={formData.uid}
                  onChange={handleChange}
                  required
                  disabled={loading || !!filament}
                  maxLength={12}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
                />
                {filament && (
                  <p className="mt-1 text-xs text-slate-500">UID cannot be changed after creation</p>
                )}
              </div>

              <div>
                <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  maxLength={100}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="material" className="block text-sm font-medium text-slate-700 mb-2">
                  Material *
                </label>
                <select
                  id="material"
                  name="material"
                  value={formData.material}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100 bg-white"
                >
                  <option value="">Select material...</option>
                  {materials.map(m => (
                    <option key={m._id} value={m._id}>
                      {m.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="materialExtra" className="block text-sm font-medium text-slate-700 mb-2">
                  Material Extra
                </label>
                <select
                  id="materialExtra"
                  name="materialExtra"
                  value={formData.materialExtra}
                  onChange={handleChange}
                  disabled={loading}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100 bg-white"
                >
                  <option value="">None</option>
                  {materialExtras.map(me => (
                    <option key={me._id} value={me._id}>
                      {getDisplayName(me.name)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Machine-Specific Settings */}
            <div className="space-y-3 border-t border-slate-200 pt-6">
              <label className="block text-sm font-medium text-slate-700">
                Machine-Specific Settings
              </label>
              <p className="text-xs text-slate-500">
                Configure filament settings for different machines and nozzle sizes
              </p>

              {formData.settings.map((setting, index) => (
                <div key={index} className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        Machine model *
                      </label>
                      <select
                        value={setting.machine}
                        onChange={(e) => handleSettingChange(index, null, 'machine', e.target.value)}
                        disabled={loading}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100 bg-white text-sm"
                      >
                        <option value="">Select machine model...</option>
                        {machineModels.map(m => (
                          <option key={m._id} value={m._id}>
                            {m.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-2">
                        Nozzle Settings
                      </label>
                      <div className="space-y-2">
                        {[
                          { key: '0_2', label: '0.2' },
                          { key: '0_4', label: '0.4' },
                          { key: '0_6', label: '0.6' },
                          { key: '0_8', label: '0.8' }
                        ].map(({ key, label }) => {
                          const nozzleSetting = setting.settings?.[key] || { inherits: null, override: '{}' };
                          let overrideData = {};
                          try {
                            overrideData = nozzleSetting.override ? JSON.parse(nozzleSetting.override) : {};
                          } catch (e) {
                            console.error(`Invalid JSON for nozzle ${label} override:`, e);
                          }
                          const hasOverrides = Object.keys(overrideData).length > 0;
                          const overrideKey = `${index}-${key}`;
                          const isOverrideExpanded = expandedOverrides[overrideKey] || false;

                          return (
                            <div key={key} className="border border-slate-300 rounded-lg p-2 bg-white">
                              {/* Nozzle label + Inherits on same row */}
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-semibold text-slate-700 whitespace-nowrap min-w-[60px]">
                                  {label}mm
                                </span>
                                <div className="flex-1">
                                  <AsyncSelect
                                    cacheOptions
                                    defaultOptions={filteredOptions[setting.machine]?.[key] || systemSettingsOptions}
                                    isClearable
                                    loadOptions={createLoadOptions(setting.machine, key)}
                                    value={
                                      nozzleSetting.inherits
                                        ? (filteredOptions[setting.machine]?.[key] || systemSettingsOptions).find(opt => opt.value === nozzleSetting.inherits)
                                          || { value: nozzleSetting.inherits, label: nozzleSetting.inherits }
                                        : null
                                    }
                                    onChange={(newValue) => {
                                      handleSettingChange(index, key, 'inherits', newValue?.value || null);
                                    }}
                                    placeholder="Search inherits..."
                                    isDisabled={loading}
                                    className="text-xs"
                                    key={`${index}-${key}-${setting.machine}-${formData.material}`}
                                  />
                                </div>
                              </div>

                              {/* Override accordion */}
                              <div className="mt-1 ml-[68px]">
                                <button
                                  type="button"
                                  onClick={() => setExpandedOverrides(prev => ({ ...prev, [overrideKey]: !prev[overrideKey] }))}
                                  className="inline-flex items-center text-xs font-medium text-slate-600 hover:text-slate-800 transition-colors"
                                >
                                  {isOverrideExpanded
                                    ? <ChevronDownIcon className="h-3.5 w-3.5 mr-1" />
                                    : <ChevronRightIcon className="h-3.5 w-3.5 mr-1" />
                                  }
                                  Overrides{hasOverrides && <span className="ml-1 text-blue-600">({Object.keys(overrideData).length})</span>}
                                </button>
                                {isOverrideExpanded && (
                                  <div className="mt-1">
                                    <JSONEditor
                                      data={overrideData}
                                      onChange={(newSettings) => handleSettingChange(index, key, 'override', newSettings)}
                                      disabled={loading}
                                      minHeight={100}
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => removeSetting(index)}
                    disabled={loading}
                    className="mt-3 inline-flex items-center px-3 py-1.5 text-sm bg-red-100 hover:bg-red-200 text-red-700 rounded-md transition-colors"
                  >
                    <TrashIcon className="h-4 w-4 mr-1" />
                    Remove
                  </button>
                </div>
              ))}

              {(() => {
                const noMaterial = !formData.material;
                const allConfigured = machineModels.length > 0 && machineModels.every(m => formData.settings.some(s => s.machine === m._id));
                const addDisabled = loading || autoLoading || noMaterial || allConfigured;
                const autoDisabled = loading || autoLoading || noMaterial || allConfigured;
                return (
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={addSetting}
                      disabled={addDisabled}
                      className="inline-flex items-center px-4 py-2 text-sm bg-green-100 hover:bg-green-200 text-green-700 rounded-lg transition-colors font-medium disabled:opacity-50"
                    >
                      <PlusIcon className="h-4 w-4 mr-1" />
                      Add Machine Setting
                    </button>
                    <button
                      type="button"
                      onClick={autoLoadConfigs}
                      disabled={autoDisabled}
                      className="inline-flex items-center px-4 py-2 text-sm bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg transition-colors font-medium disabled:opacity-50"
                    >
                      <BoltIcon className="h-4 w-4 mr-1" />
                      {autoLoading ? 'Loading...' : 'Auto-load all machine configs'}
                    </button>
                  </div>
                );
              })()}
            </div>

            {/* Notes */}
            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-slate-700 mb-2">
                Notes
              </label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                disabled={loading}
                rows={3}
                maxLength={500}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100 resize-none"
              />
            </div>

            {/* Active Setting */}
            <div>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                  disabled={loading}
                  className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-slate-700">Active</span>
              </label>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50">
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="px-5 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 transition-colors shadow-sm"
            >
              {loading ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
